import type { IANATimezoneIdentifier } from './types'

type StandardExample = {
  identifier: IANATimezoneIdentifier
  /**
   * ISO 3166-1 alpha-2 country code.
   *
   * @see https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2
   */
  region: string
}
type UTCExample = Pick<StandardExample, 'identifier'> & {
  region: null
}
/** @private Only exported for use in tests */
export type Example = StandardExample | UTCExample
type HumanName = string
type Examples = Record<HumanName, Example>

/**
 * This list is somewhat arbitrarily chosen, but is meant to cover most time
 * zones. It’s ordered by UTC offset, from most negative to most positive.
 *
 * `Zones` is only exported for use within `TimeZone`. Code outside `TimeZone`
 * that works with the list must use `timeZoneData` instead.
 *
 * Each entry should provide examples (generally major cities), with the
 * associated country code. The country code will be rendered in the user’s
 * preferred language by `formattedZoneNames`.
 *
 * Rules for examples:
 * * use the largest or most-recognizable cities in the zone
 * * at most three examples
 * * one city per country/region
 * * use the currently-accepted English name of the city (for example, ‘Kyiv’
 *   instead of ‘Kiev’)
 * * put cities in alphabetical order for code readability--UTC is the
 *   exception for this
 *
 * Zones should be consolidated when reasonable: for example, `Europe/Berlin`
 * and `Europe/Paris` both represent the UTC+1/UTC+2 offset, so Berlin and
 * Paris are grouped under a single zone.
 *
 * @note The test suite will not allow merging zones with different Daylight
 *   Saving rules, or that have different well-known abbreviations. The test
 *   suite will also detect when merged zones no longer have the same offsets.
 * @note This intentionally doesn’t declare UTC offsets within the data,
 *   because they can change at any time. Instead, rely on `Intl` data.
 * @note This intentionally doesn’t declare abbreviations for zone names;
 *   instead, rely on `Intl` data.
 *
 * @see https://commons.wikimedia.org/wiki/File:World_Time_Zones_Map.png
 *
 * @private Do not access this directly--use `timeZoneData()` instead.
 */
export const Zones = {
  /** UTC-10, no daylight saving */
  'Pacific/Honolulu': {
    Hawaii: {
      identifier: 'Pacific/Honolulu',
      region: 'US',
    },
  },
  /** UTC-9, UTC-8 */
  'America/Anchorage': {
    Alaska: {
      identifier: 'America/Anchorage',
      region: 'US',
    },
  },
  /** UTC-8, UTC-7 */
  'America/Los_Angeles': {
    'Los Angeles': {
      identifier: 'America/Los_Angeles',
      region: 'US',
    },
    Vancouver: {
      identifier: 'America/Vancouver',
      region: 'CA',
    },
  },
  /** UTC-7, no daylight saving */
  'America/Phoenix': {
    Phoenix: {
      identifier: 'America/Phoenix',
      region: 'US',
    },
  },
  /** UTC-7, UTC-6 */
  'America/Denver': {
    Denver: {
      identifier: 'America/Denver',
      region: 'US',
    },
  },
  /** UTC-6, no daylight saving */
  'America/Mexico_City': {
    'Mexico City': {
      identifier: 'America/Mexico_City',
      region: 'MX',
    },
  },
  /** UTC-6, UTC-5 */
  'America/Chicago': {
    Chicago: {
      identifier: 'America/Chicago',
      region: 'US',
    },
  },
  /** UTC-5, no daylight saving */
  'America/Bogota': {
    Bogota: {
      identifier: 'America/Bogota',
      region: 'CO',
    },
    Lima: {
      identifier: 'America/Lima',
      region: 'PE',
    },
  },
  /** UTC-5, UTC-4 */
  'America/New_York': {
    'New York City': {
      identifier: 'America/New_York',
      region: 'US',
    },
    Toronto: {
      identifier: 'America/Toronto',
      region: 'CA',
    },
  },
  /** UTC-4, no daylight saving */
  'America/Caracas': {
    Bridgetown: {
      identifier: 'America/Barbados',
      region: 'BB',
    },
    Caracas: {
      identifier: 'America/Caracas',
      region: 'VE',
    },
  },
  /** UTC-4, UTC-3 */
  'America/Santiago': {
    Santiago: {
      identifier: 'America/Santiago',
      region: 'CL',
    },
  },
  /** UTC-4, UTC-3 */
  'America/Halifax': {
    Halifax: {
      identifier: 'America/Halifax',
      region: 'CA',
    },
  },
  /** UTC-3, daylight saving unpredictable */
  'America/Argentina/Buenos_Aires': {
    'Buenos Aires': {
      identifier: 'America/Argentina/Buenos_Aires',
      region: 'AR',
    },
  },
  /** UTC-3, no daylight saving */
  'America/Sao_Paulo': {
    'São Paulo': {
      identifier: 'America/Sao_Paulo',
      region: 'BR',
    },
    Montevideo: {
      identifier: 'America/Montevideo',
      region: 'UY',
    },
  },
  /**
   * Technically UTC and GMT are different things, but the difference doesn't
   * matter for our purposes.
   */
  'Etc/UTC': {
    UTC: {
      identifier: 'Etc/UTC',
      region: null,
    },
    GMT: {
      identifier: 'Etc/GMT',
      region: 'GB',
    },
  },
  /** UTC+0, no daylight saving */
  'Africa/Abidjan': {
    Abidjan: {
      identifier: 'Africa/Abidjan',
      region: 'CI',
    },
    Accra: {
      identifier: 'Africa/Accra',
      region: 'GH',
    },
    Dakar: {
      identifier: 'Africa/Dakar',
      region: 'SN',
    },
  },
  /** UTC+0, UTC+1 */
  'Europe/London': {
    Dublin: {
      identifier: 'Europe/Dublin',
      region: 'IE',
    },
    Lisbon: {
      identifier: 'Europe/Lisbon',
      region: 'PT',
    },
    London: {
      identifier: 'Europe/London',
      region: 'GB',
    },
  },
  /** UTC+1, no daylight saving */
  'Africa/Lagos': {
    Algiers: {
      identifier: 'Africa/Algiers',
      region: 'DZ',
    },
    Kinshasa: {
      identifier: 'Africa/Kinshasa',
      region: 'CD',
    },
    Lagos: {
      identifier: 'Africa/Lagos',
      region: 'NG',
    },
  },
  /** UTC+1, UTC+2 */
  'Europe/Berlin': {
    Berlin: {
      identifier: 'Europe/Berlin',
      region: 'DE',
    },
    Madrid: {
      identifier: 'Europe/Madrid',
      region: 'ES',
    },
    Paris: {
      identifier: 'Europe/Paris',
      region: 'FR',
    },
  },
  /** UTC+2, no daylight saving */
  'Africa/Johannesburg': {
    Johannesburg: {
      identifier: 'Africa/Johannesburg',
      region: 'ZA',
    },
    Khartoum: {
      identifier: 'Africa/Khartoum',
      region: 'SD',
    },
  },
  /** UTC+2, UTC+3 */
  'Europe/Kyiv': {
    Athens: {
      identifier: 'Europe/Athens',
      region: 'GR',
    },
    Cairo: {
      identifier: 'Africa/Cairo',
      region: 'EG',
    },
    Kyiv: {
      identifier: 'Europe/Kyiv',
      region: 'UA',
    },
  },
  /** UTC+3, no daylight saving */
  'Europe/Moscow': {
    Istanbul: {
      identifier: 'Europe/Istanbul',
      region: 'TR',
    },
    Moscow: {
      identifier: 'Europe/Moscow',
      region: 'RU',
    },
    Nairobi: {
      identifier: 'Africa/Nairobi',
      region: 'KE',
    },
  },
  /** UTC+3:30, no daylight saving */
  'Asia/Tehran': {
    Tehran: {
      identifier: 'Asia/Tehran',
      region: 'IR',
    },
  },
  /** UTC+4, no daylight saving */
  'Asia/Dubai': {
    Dubai: {
      identifier: 'Asia/Dubai',
      region: 'AE',
    },
  },
  /** UTC+4:30, no daylight saving */
  'Asia/Kabul': {
    Kabul: {
      identifier: 'Asia/Kabul',
      region: 'AF',
    },
  },
  /** UTC+5, no daylight saving */
  'Asia/Karachi': {
    Almaty: {
      identifier: 'Asia/Almaty',
      region: 'KZ',
    },
    Karachi: {
      identifier: 'Asia/Karachi',
      region: 'PK',
    },
    Tashkent: {
      identifier: 'Asia/Tashkent',
      region: 'UZ',
    },
  },
  /** UTC+5:30, no daylight saving */
  'Asia/Kolkata': {
    Mumbai: {
      identifier: 'Asia/Kolkata',
      region: 'IN',
    },
  },
  /** UTC+5:45, no daylight saving */
  'Asia/Kathmandu': {
    Kathmandu: {
      identifier: 'Asia/Kathmandu',
      region: 'NP',
    },
  },
  /** UTC+6, no daylight saving */
  'Asia/Dhaka': {
    Dhaka: {
      identifier: 'Asia/Dhaka',
      region: 'BD',
    },
  },
  /** UTC+6:30, no daylight saving */
  'Asia/Yangon': {
    // Nay Pyi Taw is the new capital city as of 2012, but has a smaller population than Yangon.
    Yangon: {
      identifier: 'Asia/Yangon',
      region: 'MM',
    },
  },
  /** UTC+7, no daylight saving */
  'Asia/Jakarta': {
    Bangkok: {
      identifier: 'Asia/Bangkok',
      region: 'TH',
    },
    Hanoi: {
      identifier: 'Asia/Ho_Chi_Minh',
      region: 'VN',
    },
    Jakarta: {
      identifier: 'Asia/Jakarta',
      region: 'ID',
    },
  },
  /** UTC+8, no daylight saving */
  'Asia/Shanghai': {
    Beijing: {
      identifier: 'Asia/Shanghai',
      region: 'CN',
    },
    Manila: {
      identifier: 'Asia/Manila',
      region: 'PH',
    },
    Taipei: {
      identifier: 'Asia/Taipei',
      region: 'TW',
    },
  },
  /** UTC+8, no daylight saving */
  'Australia/Perth': {
    Perth: {
      identifier: 'Australia/Perth',
      region: 'AU',
    },
  },
  /** UTC+9, no daylight saving */
  'Asia/Tokyo': {
    Seoul: {
      identifier: 'Asia/Seoul',
      region: 'KR',
    },
    Tokyo: {
      identifier: 'Asia/Tokyo',
      region: 'JP',
    },
  },
  /** UTC+9:30, no daylight saving */
  'Australia/Darwin': {
    Darwin: {
      identifier: 'Australia/Darwin',
      region: 'AU',
    },
  },
  /** UTC+9:30, UTC+10:30 */
  'Australia/Adelaide': {
    Adelaide: {
      identifier: 'Australia/Adelaide',
      region: 'AU',
    },
  },
  /** UTC+10, no daylight saving */
  'Australia/Brisbane': {
    Brisbane: {
      identifier: 'Australia/Brisbane',
      region: 'AU',
    },
    'Port Moresby': {
      identifier: 'Pacific/Port_Moresby',
      region: 'PG',
    },
  },
  /** UTC+10, UTC+11 */
  'Australia/Sydney': {
    Sydney: {
      identifier: 'Australia/Sydney',
      region: 'AU',
    },
  },
  /** UTC+12, no daylight saving */
  'Pacific/Fiji': {
    Suva: {
      identifier: 'Pacific/Fiji',
      region: 'FJ',
    },
  },
  /** UTC+12, UTC+13 */
  'Pacific/Auckland': {
    Auckland: {
      identifier: 'Pacific/Auckland',
      region: 'NZ',
    },
  },
} as const satisfies Record<IANATimezoneIdentifier, Examples>

export type SupportedTimeZoneIdentifier = keyof typeof Zones

/**
 * These are time zones that we used to display in the UI, but which have been
 * merged into another zone in `Zones`. The deprecated zones are mapped to
 * current zones.
 *
 * @private Do not access this directly--use
 *   `canonicalIANATimezoneIdentifier()` instead.
 */
export const DeprecatedZones = {
  'Asia/Hong_Kong': 'Asia/Shanghai',
  'Asia/Baghdad': 'Europe/Moscow',
  /** offset changed from permanent UTC-6 to permanent UTC-5 on 2023-03-01 */
  'Asia/Almaty': 'Asia/Karachi',
  UTC: 'Etc/UTC',
} as const satisfies Record<IANATimezoneIdentifier, SupportedTimeZoneIdentifier>

export type UnsupportedTimeZoneIdentifier = keyof typeof DeprecatedZones
